// app/javascript/components/CustomMessageInput.jsx

import React, {useEffect, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPaperPlane} from "@fortawesome/free-regular-svg-icons";
import {faPaperclip} from "@fortawesome/free-solid-svg-icons";
import {scrollToBottom} from "../utils/messageUtils";
import {useGroupChannelContext} from "@sendbird/uikit-react/GroupChannel/context";


export default function CustomMessageInput({conversationId, fallbackMode, onMessageSent}) {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const textareaRef = useRef(null);
    const trimmedMessage = message.trim();
    const hasText = trimmedMessage.length > 0;
    const isSendDisabled = sending || !hasText;

    const currentChannel = fallbackMode ? null : useGroupChannelContext();

    function buildPayload({text = null, file = null}) {
        let formData = new FormData()
        if (text) formData.append('message', text)
        if (file) formData.append('file', file)

        return formData;
    }

    async function sendMessageRequest(payload) {
        return $.ajax({
            url: `/conversations/${conversationId}/send_message`,
            method: 'POST',
            dataType: 'json',
            data: payload,
            processData: false,
            contentType: false,
        });
    }

    async function sendMessage(payload) {
        setSending(true);
        try {
            const result = await sendMessageRequest(payload);
            handleSendSuccess(result);
        } catch (err) {
            window.sentry?.captureException(err);
        } finally {
            setSending(false);
        }
    }

    function handleSendSuccess(result) {
        setMessage('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
        scrollToBottom();
        if (typeof onMessageSent === 'function') {
            onMessageSent(result);
        }
    }

    async function handleSend() {
        if (isSendDisabled) return;
        if (!fallbackMode) {
            setSending(true);
            currentChannel.sendUserMessage({message: trimmedMessage}).then(() => {
                setSending(false);
                handleSendSuccess()
            }).catch((err) => {
                setSending(false);
            })
        } else {
            await sendMessage(buildPayload({text: trimmedMessage}));
        }
    }

    function onInputChange(e) {
        setMessage(e.target.value);
        if (textareaRef.current) {
            const el = textareaRef.current;
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.3)}px`;
        }
    }

    function handleAttachmentClick() {
        const input = document.createElement('input');
        input.type = 'file';
        input.click();
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            if (!fallbackMode) {
                setSending(true);
                currentChannel.sendFileMessage({file: file}).then(() => {
                    setSending(false);
                    handleSendSuccess()
                }).catch((err) => {
                    setSending(false);
                });
            } else {
                await sendMessage(buildPayload({file}));
            }
        };
    }

    useEffect(() => {
        if (textareaRef.current) {
            const el = textareaRef.current;
            el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.3)}px`;
        }
    }, []);

    return (
        <div className="sendbird-limited-service-input-container">
            <div style={{position: 'relative', width: '100%'}}>
                <textarea
                    ref={textareaRef}
                    placeholder="Enter message"
                    className="sendbird-limited-service-input"
                    value={message}
                    disabled={sending}
                    onChange={onInputChange}
                    rows={1}
                />
                {!hasText && (
                    <button
                        className="sendbird-limited-service-attachment-button"
                        onClick={handleAttachmentClick}
                        aria-label="Add attachment"
                        disabled={sending}
                    >
                        <FontAwesomeIcon icon={faPaperclip}/>
                    </button>
                )}
                {hasText && (
                    <button
                        className="sendbird-limited-service-send-button"
                        onClick={handleSend}
                        aria-label="Send"
                        disabled={isSendDisabled}
                    >
                        <FontAwesomeIcon icon={faPaperPlane}/>
                    </button>
                )}
            </div>
        </div>
    );
}
